/**
 * FormValidator - A drop-in replacement for Syncfusion's FormValidator
 * Provides form validation functionality using HTML5 validation and custom rules
 */

// Support both tuple and array formats for Syncfusion compatibility
type RuleTuple<T> = [T, string?] | (T | string)[];
type CustomValidator = (value: string, element: HTMLInputElement) => boolean;
type RangeValidator = (args: any) => boolean;

export interface ValidationRule {
  required?: RuleTuple<boolean>;
  email?: RuleTuple<boolean>;
  minLength?: RuleTuple<number>;
  maxLength?: RuleTuple<number>;
  min?: RuleTuple<number>;
  max?: RuleTuple<number>;
  pattern?: RuleTuple<string | RegExp>;
  regex?: RuleTuple<string | RegExp>; // Alias for pattern (Syncfusion compatibility)
  equalTo?: RuleTuple<string>;
  range?: RuleTuple<string | RangeValidator>; // For custom range/equalTo validation
  custom?: RuleTuple<CustomValidator>;
}

export interface ValidationRules {
  [fieldName: string]: ValidationRule;
}

export interface FormValidatorOptions {
  rules?: ValidationRules;
  validationComplete?: (args: { status: string; errorElement?: HTMLElement; inputElement?: HTMLElement }) => void;
  customPlacement?: (inputElement: HTMLInputElement, errorElement: HTMLElement) => void;
  focusout?: (args: { element: HTMLElement }) => void;
  keyup?: (args: { element: HTMLElement }) => void;
  click?: (args: { element: HTMLElement }) => void;
  change?: (args: { element: HTMLElement }) => void;
  submit?: (args: { event: Event }) => void;
}

export class FormValidator {
  private formElement: HTMLFormElement | null = null;
  private rules: ValidationRules = {};
  private errors: Map<string, string> = new Map();
  private options: FormValidatorOptions;

  constructor(formSelector: string | HTMLFormElement, options: FormValidatorOptions = {}) {
    this.options = options;
    this.rules = options.rules || {};

    if (typeof formSelector === "string") {
      this.formElement = document.querySelector(formSelector) as HTMLFormElement;
    } else {
      this.formElement = formSelector;
    }

    if (this.formElement) {
      this.attachEvents();
    }
  }

  private attachEvents(): void {
    if (!this.formElement) return;

    // Prevent default form submission
    this.formElement.addEventListener("submit", (e) => {
      e.preventDefault();
      if (this.options.submit) {
        this.options.submit({ event: e });
      }
    });

    // Attach events to inputs
    const inputs = this.formElement.querySelectorAll("input, select, textarea");
    inputs.forEach((input) => {
      const htmlInput = input as HTMLInputElement;

      if (this.options.focusout) {
        htmlInput.addEventListener("blur", () => {
          this.validateField(htmlInput.name);
          this.options.focusout!({ element: htmlInput });
        });
      }

      if (this.options.keyup) {
        htmlInput.addEventListener("keyup", () => {
          this.options.keyup!({ element: htmlInput });
        });
      }

      if (this.options.change) {
        htmlInput.addEventListener("change", () => {
          this.validateField(htmlInput.name);
          this.options.change!({ element: htmlInput });
        });
      }

      if (this.options.click) {
        htmlInput.addEventListener("click", () => {
          this.options.click!({ element: htmlInput });
        });
      }
    });
  }

  /**
   * Add validation rules for a field
   */
  addRules(fieldName: string, rules: ValidationRule): void {
    this.rules[fieldName] = { ...this.rules[fieldName], ...rules };
  }

  /**
   * Remove validation rules for a field
   */
  removeRules(fieldName: string, ruleNames?: string[]): void {
    if (!ruleNames) {
      delete this.rules[fieldName];
    } else {
      ruleNames.forEach((ruleName) => {
        delete (this.rules[fieldName] as any)?.[ruleName];
      });
    }
  }

  /**
   * Validate a single field
   */
  validateField(fieldName: string): boolean {
    if (!this.formElement) return true;

    const input = this.formElement.querySelector(`[name="${fieldName}"]`) as HTMLInputElement;
    if (!input) return true;

    const fieldRules = this.rules[fieldName];
    if (!fieldRules) return true;

    const value = input.value;
    let isValid = true;
    let errorMessage = "";

    // Required validation
    if (fieldRules.required) {
      const arr = fieldRules.required as (boolean | string)[];
      const required = arr[0] as boolean;
      const message = arr[1] as string | undefined;
      if (required && !value.trim()) {
        isValid = false;
        errorMessage = message || "This field is required";
      }
    }

    // Email validation
    if (isValid && fieldRules.email) {
      const arr = fieldRules.email as (boolean | string)[];
      const enabled = arr[0] as boolean;
      const message = arr[1] as string | undefined;
      if (enabled && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          isValid = false;
          errorMessage = message || "Please enter a valid email address";
        }
      }
    }

    // Min length validation
    if (isValid && fieldRules.minLength) {
      const arr = fieldRules.minLength as (number | string)[];
      const minLen = arr[0] as number;
      const message = arr[1] as string | undefined;
      if (value.length < minLen) {
        isValid = false;
        errorMessage = message || `Minimum ${minLen} characters required`;
      }
    }

    // Max length validation
    if (isValid && fieldRules.maxLength) {
      const arr = fieldRules.maxLength as (number | string)[];
      const maxLen = arr[0] as number;
      const message = arr[1] as string | undefined;
      if (value.length > maxLen) {
        isValid = false;
        errorMessage = message || `Maximum ${maxLen} characters allowed`;
      }
    }

    // Min value validation
    if (isValid && fieldRules.min) {
      const arr = fieldRules.min as (number | string)[];
      const minVal = arr[0] as number;
      const message = arr[1] as string | undefined;
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue < minVal) {
        isValid = false;
        errorMessage = message || `Minimum value is ${minVal}`;
      }
    }

    // Max value validation
    if (isValid && fieldRules.max) {
      const arr = fieldRules.max as (number | string)[];
      const maxVal = arr[0] as number;
      const message = arr[1] as string | undefined;
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue > maxVal) {
        isValid = false;
        errorMessage = message || `Maximum value is ${maxVal}`;
      }
    }

    // Pattern validation (also supports 'regex' alias)
    const patternRule = fieldRules.pattern || fieldRules.regex;
    if (isValid && patternRule) {
      const [pattern, message] = patternRule as [string | RegExp, string?];
      const regex = typeof pattern === "string" ? new RegExp(pattern) : pattern;
      if (value && !regex.test(value)) {
        isValid = false;
        errorMessage = (message as string) || "Please match the requested format";
      }
    }

    // Equal to validation
    if (isValid && fieldRules.equalTo) {
      const [targetFieldName, message] = fieldRules.equalTo as [string, string?];
      const targetInput = this.formElement.querySelector(`[name="${targetFieldName}"]`) as HTMLInputElement;
      if (targetInput && value !== targetInput.value) {
        isValid = false;
        errorMessage = (message as string) || "Values do not match";
      }
    }

    // Range validation (Syncfusion uses this for custom validators like password match)
    if (isValid && fieldRules.range) {
      const [validator, message] = fieldRules.range as [string | ((args: any) => boolean), string?];
      if (typeof validator === "function") {
        if (!validator({ value, element: input })) {
          isValid = false;
          errorMessage = (message as string) || "Invalid value";
        }
      }
    }

    // Custom validation
    if (isValid && fieldRules.custom) {
      const arr = fieldRules.custom as (CustomValidator | string)[];
      const validator = arr[0] as CustomValidator;
      const message = arr[1] as string | undefined;
      if (typeof validator === "function" && !validator(value, input)) {
        isValid = false;
        errorMessage = message || "Invalid value";
      }
    }

    // Update error state
    if (!isValid) {
      this.errors.set(fieldName, errorMessage);
      this.showError(input, errorMessage);
    } else {
      this.errors.delete(fieldName);
      this.hideError(input);
    }

    // Callback
    if (this.options.validationComplete) {
      this.options.validationComplete({
        status: isValid ? "success" : "failure",
        inputElement: input,
      });
    }

    return isValid;
  }

  /**
   * Validate all fields in the form
   */
  validate(): boolean {
    let isFormValid = true;

    Object.keys(this.rules).forEach((fieldName) => {
      const isFieldValid = this.validateField(fieldName);
      if (!isFieldValid) {
        isFormValid = false;
      }
    });

    return isFormValid;
  }

  /**
   * Get error messages
   */
  getErrors(): Record<string, string> {
    const errors: Record<string, string> = {};
    this.errors.forEach((value, key) => {
      errors[key] = value;
    });
    return errors;
  }

  /**
   * Reset all validation errors
   */
  reset(): void {
    this.errors.clear();
    if (this.formElement) {
      const errorElements = this.formElement.querySelectorAll(".e-error, .validation-error");
      errorElements.forEach((el) => el.remove());

      const inputs = this.formElement.querySelectorAll("input, select, textarea");
      inputs.forEach((input) => {
        input.classList.remove("e-error", "is-invalid");
      });
    }
  }

  /**
   * Destroy the validator
   */
  destroy(): void {
    this.reset();
    this.rules = {};
    this.formElement = null;
  }

  private showError(input: HTMLInputElement, message: string): void {
    input.classList.add("e-error", "is-invalid");

    // Remove existing error message
    const existingError = input.parentElement?.querySelector(".validation-error");
    if (existingError) {
      existingError.textContent = message;
      return;
    }

    // Create error element
    const errorElement = document.createElement("span");
    errorElement.className = "validation-error e-error";
    errorElement.style.cssText = "color: #d32f2f; font-size: 0.75rem; margin-top: 0.25rem; display: block;";
    errorElement.textContent = message;

    if (this.options.customPlacement) {
      this.options.customPlacement(input, errorElement);
    } else {
      input.parentElement?.appendChild(errorElement);
    }
  }

  private hideError(input: HTMLInputElement): void {
    input.classList.remove("e-error", "is-invalid");

    const errorElement = input.parentElement?.querySelector(".validation-error");
    if (errorElement) {
      errorElement.remove();
    }
  }
}

export default FormValidator;
